import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Tooltip,
  CircularProgress,
  TextField,
  Button
} from '@mui/material';
import { updateClientAssignmentAlgorithm, updateTherapistAutoAssignment, getAssignmentStats, getPlatformSettings, updatePlatformSettings } from '../../services/api';

const TherapistAssignmentSettings = () => {
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [algorithm, setAlgorithm] = useState('round_robin');
  const [therapists, setTherapists] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Capacity settings
  const [defaultTherapistMaxClients, setDefaultTherapistMaxClients] = useState(20);
  const [defaultSupervisorMaxTherapists, setDefaultSupervisorMaxTherapists] = useState(10);
  const [capacitySettings, setCapacitySettings] = useState(null);

  // Get stats and current settings
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statsData, platformSettings] = await Promise.all([
          getAssignmentStats(),
          getPlatformSettings(localStorage.getItem('token'))
        ]);

        setStats(statsData);
        setTherapists(statsData.therapistStats || []);
        setAlgorithm(statsData.currentAlgorithm || 'round_robin');

        // Load capacity settings
        if (platformSettings?.clientAssignmentSettings) {
          const capacityConfig = platformSettings.clientAssignmentSettings;
          setDefaultTherapistMaxClients(capacityConfig.defaultTherapistMaxClients || 20);
          setDefaultSupervisorMaxTherapists(capacityConfig.defaultSupervisorMaxTherapists || 10);
          setCapacitySettings(capacityConfig);
        }

        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch assignment stats:', err);
        setError('Failed to load therapist assignment data');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle algorithm change
  const handleAlgorithmChange = async (event) => {
    const newAlgorithm = event.target.value;
    setAlgorithm(newAlgorithm);
    
    try {
      await updateClientAssignmentAlgorithm(newAlgorithm, localStorage.getItem('token'));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update assignment algorithm:', err);
      setError('Failed to update assignment algorithm');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle auto-assignment toggle
  const handleAutoAssignmentToggle = async (therapistId, currentValue) => {
    try {
      // Optimistically update UI
      setTherapists(therapists.map(t => 
        t.id === therapistId 
          ? {...t, canReceiveAutoAssignments: !currentValue} 
          : t
      ));
      
      // Update on server
      await updateTherapistAutoAssignment(
        therapistId, 
        !currentValue, 
        localStorage.getItem('token')
      );
    } catch (err) {
      console.error('Failed to update therapist auto-assignment:', err);
      
      // Revert UI on error
      setTherapists(therapists.map(t => 
        t.id === therapistId 
          ? {...t, canReceiveAutoAssignments: currentValue} 
          : t
      ));
      
      setError('Failed to update therapist auto-assignment setting');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Refresh stats
  const refreshStats = async () => {
    try {
      setStatsLoading(true);
      const data = await getAssignmentStats();
      setStats(data);
      setTherapists(data.therapistStats || []);
      setStatsLoading(false);
    } catch (err) {
      console.error('Failed to refresh assignment stats:', err);
      setError('Failed to refresh assignment statistics');
      setStatsLoading(false);
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle capacity settings save
  const handleSaveCapacitySettings = async () => {
    try {
      // Validate inputs
      if (defaultTherapistMaxClients < 1 || defaultTherapistMaxClients > 100) {
        setError('Therapist max clients must be between 1 and 100');
        setTimeout(() => setError(null), 3000);
        return;
      }

      if (defaultSupervisorMaxTherapists < 1 || defaultSupervisorMaxTherapists > 50) {
        setError('Supervisor max therapists must be between 1 and 50');
        setTimeout(() => setError(null), 3000);
        return;
      }

      const updatedSettings = {
        clientAssignmentSettings: {
          algorithm: algorithm,
          defaultTherapistMaxClients: defaultTherapistMaxClients,
          defaultSupervisorMaxTherapists: defaultSupervisorMaxTherapists
        }
      };

      await updatePlatformSettings(updatedSettings, localStorage.getItem('token'));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save capacity settings:', err);
      setError('Failed to save capacity settings');
      setTimeout(() => setError(null), 3000);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Client Assignment Settings
      </Typography>
      
      {error && (
        <Box sx={{ mb: 2, p: 1, bgcolor: 'error.main', color: 'error.contrastText', borderRadius: 1 }}>
          <Typography>{error}</Typography>
        </Box>
      )}
      
      {saveSuccess && (
        <Box sx={{ mb: 2, p: 1, bgcolor: 'success.main', color: 'success.contrastText', borderRadius: 1 }}>
          <Typography>Settings updated successfully</Typography>
        </Box>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Assignment Algorithm
              </Typography>
              <FormControl component="fieldset">
                <FormLabel component="legend">Select how new clients are assigned to therapists:</FormLabel>
                <RadioGroup
                  aria-label="assignment-algorithm"
                  name="algorithm"
                  value={algorithm}
                  onChange={handleAlgorithmChange}
                >
                  <FormControlLabel 
                    value="round_robin" 
                    control={<Radio />} 
                    label="Round Robin (based on therapist's last assignment date)" 
                  />
                  <FormControlLabel 
                    value="least_clients" 
                    control={<Radio />} 
                    label="Least Clients (therapist with fewest assigned clients)" 
                  />
                </RadioGroup>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Assignment Statistics
                {statsLoading && <CircularProgress size={20} sx={{ ml: 2 }} />}
              </Typography>
              {stats && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Total Therapists:
                      </Typography>
                      <Typography variant="body1">
                        {stats.totalTherapists}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Active Therapists:
                      </Typography>
                      <Typography variant="body1">
                        {stats.activeTherapists}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Eligible for Assignment:
                      </Typography>
                      <Typography variant="body1">
                        {stats.eligibleForAssignment}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Total Clients:
                      </Typography>
                      <Typography variant="body1">
                        {stats.totalClients}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Assigned Clients:
                      </Typography>
                      <Typography variant="body1">
                        {stats.assignedClients}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Unassigned Clients:
                      </Typography>
                      <Typography variant="body1">
                        {stats.unassignedClients}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Default Capacity Limits
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Configure default capacity limits for therapists and supervisors. These defaults apply when creating new staff members, but can be overridden at the individual level.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Default Therapist Max Clients"
                    value={defaultTherapistMaxClients}
                    onChange={(e) => setDefaultTherapistMaxClients(parseInt(e.target.value) || 20)}
                    inputProps={{ min: 1, max: 100 }}
                    helperText="Default maximum number of clients per therapist (1-100)"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Default Supervisor Max Therapists"
                    value={defaultSupervisorMaxTherapists}
                    onChange={(e) => setDefaultSupervisorMaxTherapists(parseInt(e.target.value) || 10)}
                    inputProps={{ min: 1, max: 50 }}
                    helperText="Default maximum number of therapists per supervisor (1-50)"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleSaveCapacitySettings}
                    sx={{ mt: 1 }}
                  >
                    Save Capacity Settings
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Therapist Assignment Settings
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Therapist</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Clients</TableCell>
                      <TableCell>Capacity</TableCell>
                      <TableCell>Last Assignment</TableCell>
                      <TableCell>Can Receive Auto Assignments</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {therapists.map((therapist) => (
                      <TableRow key={therapist.id}>
                        <TableCell>
                          <Typography variant="body2">{therapist.name}</Typography>
                          <Typography variant="caption" color="textSecondary">{therapist.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              bgcolor: therapist.status === 'Active' ? 'success.main' : 'warning.main',
                              color: 'white',
                              p: 0.5,
                              borderRadius: 1,
                              display: 'inline-block'
                            }}
                          >
                            {therapist.status}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {therapist.clientCount} / {therapist.maxCapacity}
                        </TableCell>
                        <TableCell>
                          {therapist.maxCapacity}
                        </TableCell>
                        <TableCell>
                          {therapist.lastAssignmentDate 
                            ? new Date(therapist.lastAssignmentDate).toLocaleString() 
                            : 'Never'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={therapist.status !== 'Active' ? 'Therapist must be Active to receive assignments' : ''}>
                            <span>
                              <Switch
                                checked={therapist.canReceiveAutoAssignments}
                                onChange={() => handleAutoAssignmentToggle(therapist.id, therapist.canReceiveAutoAssignments)}
                                disabled={therapist.status !== 'Active'}
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TherapistAssignmentSettings;
